import axios, { AxiosError, AxiosResponse } from 'axios'
import type { SearchResultDto } from '../models/search'
import type { ContentPhotoDto, PhotoCommentDto } from '../models/contentPhoto'
import type { DiscoverResult, RecentUserDto } from '../models/discover'
import { toast } from 'react-toastify'
import { history } from '../history'
import { Activity, ActivityFormValues } from '../models/activity'
import { Post, PostCommentDto, PostFormValues } from '../models/post'
import { Arduino } from '../models/arduino'
import { ArduinoHistory } from '../models/arduinoHistory'
import { CartridgeSpecDto, CartridgeSpecFormValues, AmmoProductDto, AmmoProductFormValues } from '../models/ammo'
import type { ReloaderRecipeDto, ReloaderRecipeFormValues, RecipeValidationData } from '../models/reloaderRecipe'
import type { SlopPostDto, SlopPostCommentDto } from '../models/slopPost'
import type { MixedFeedResultDto } from '../models/mixedFeed'
import type { ShootDto, ShootLikeResultDto } from '../models/shoot'
import { Firearm, FirearmFormValues, FirearmImageDto } from '../models/firearm'
import { FirearmModel, FirearmModelFormValues } from '../models/firearmModel'
import { Part, PartFormValues } from '../models/part'
import { FirearmBuild, FirearmBuildFormValues } from '../models/build'
import { Photo, Profile, ProfileActivityItemDto } from '../models/profile'
import { User, UserFormValues } from '../models/user'
import {
  ModerationLogDto,
  ModerationLogParams,
  ModeratorSummaryDto
} from '../models/moderation'
import type { FlagDto, FlagListParams } from '../models/flag'
import { PagedResult } from '../models/pagedResult'
import type { MotdDto, MotdHistoryItemDto } from '../models/motd'
import type { FeatureFlagDto } from '../models/featureFlag'
import type { ConsentTextConfigDto, ConsentTextFormValues } from '../models/consentText'
import type { LegalTextDto, LegalTextFormValues } from '../models/legalText'
import type { PagedResult } from '../models/pagedResult'
import {
  OrphanedContentDto,
  OrphanedContentParams,
  ReassignContentBody,
  BulkReassignBody,
  BulkReassignResult,
  AdminActionLogDto,
  PaginationParams,
  InternalTagSummaryDto,
  InternalTagContentDto
} from '../models/admin'
import { TagDto } from '../models/tag'
import { store } from '../stores/store'
import { resolveApiBaseUrl } from './resolveApiBaseUrl'
import type { UserFirearmDto } from '../models/gunVault'
import type { RegistryCommentDto } from '../models/registryComment'

export interface NotificationItemDto {
  id: string;
  actorUsername: string;
  actorInitials: string;
  type: string;
  targetType: string;
  targetId: string | null;
  message: string;
  isRead: boolean;
  createdAt: string;
}

const sleep = (delay: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, delay)
  })
}

axios.defaults.baseURL = resolveApiBaseUrl()

axios.interceptors.request.use(config => {
  const token = store.commonStore.token
  if (token) {
    const io = config.headers
    io!.Authorization = `Bearer ${token}` // needed to declare that this is not going to be null to suppress error.
  }
  return config
})

axios.interceptors.response.use(async response => {
  if (process.env.NODE_ENV === 'development') await sleep(1000)
  return response
}, (error: AxiosError<any>) => {
  const response = error.response
  if (!response) return Promise.reject(error)
  const { data, status, config } = response
  switch (status) {
    case 413: {
      toast.error('File is too large to upload. Please use a smaller image.')
      break
    }
    case 429: {
      toast.warning(data?.detail || 'You are sending requests too quickly. Please wait a moment.', { autoClose: 5000 })
      break
    }
    case 400: {
      if (typeof data === 'string') {
        toast.error(data)
      } else if (data?.detail) {
        toast.error(data.detail)
      }
      if (config.method === 'get' && data?.errors?.hasOwnProperty('id')) {
        history.push('/not-found')
      }
      if (data?.errors) {
        const modalStateErrors = []
        for (const key in data.errors) {
          if (data.errors[key]) {
            modalStateErrors.push(data.errors[key])
          }
        }
        throw modalStateErrors.flat()
      }
      break
    }
    case 401: {
      // Do not toast: 401 is routine for anonymous users (public pages still fire optional
      // requests) and for failed login (LoginForm shows inline errors). Call sites that need
      // messaging should handle it (e.g. userStore.getUser clears stale tokens silently).
      break
    }
    case 404: {
      history.push('/not-found')
      break
    }
    case 500: {
      const url = config.url || ''
      // Background polls — never send the user to the server-error screen.
      if (
        url.includes('/notifications/unread-count') ||
        url.includes('/flags/open-count')
      ) {
        break
      }
      // For registration and account flows, keep the user on the form and surface a toast instead of redirecting.
      if (url.includes('/account/register') || url.includes('/account/forgot-password') || url.includes('/account/reset-password')) {
        const message =
          data?.detail ||
          'Something went wrong processing your request. Please try again in a moment.'
        toast.error(message)
        break
      }
      store.commonStore.setServerError(data)
      history.push('/server-error')
      break
    }
  }
  return Promise.reject(error)
})

const responseBody = <T>(response: AxiosResponse<T>) => response.data

const requests = {
  get: <T>(url: string, config?: { params?: Record<string, unknown> }) =>
    axios.get<T>(url, config).then(responseBody),
  post: <T>(url: string, body: {}) =>
    axios.post<any>(url, body).then(responseBody),
  put: <T>(url: string, body: {}) =>
    axios.put<any>(url, body).then(responseBody),
  patch: <T>(url: string, body: {}) =>
    axios.patch<T>(url, body).then(responseBody),
  del: <T>(url: string) => axios.delete<T>(url).then(responseBody),
  delWithBody: <T>(url: string, body: {}) =>
    axios.delete<T>(url, { data: body }).then(responseBody)
}

const Activities = {
  list: () => requests.get<Activity[]>('/activities'),
  details: (id: string) => requests.get<Activity>(`/activities/${id}`),
  create: (activity: ActivityFormValues) => requests.post<void>('/activities', activity),
  update: (activity: ActivityFormValues) => requests.put<void>(`/activities/${activity.id}`, activity),
  delete: (id: string) => requests.del<void>(`/activities/${id}`),
  attend: (id: string) => requests.post<void>(`/activities/${id}/attend`, {})
}

const Posts = {
  list: (params?: { type?: number; firearmModelId?: string; partId?: string; ammoProductId?: string; tag?: string; tagIds?: string[]; tagMatch?: string; authorUsername?: string; pageNumber?: number; pageSize?: number; sortBy?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.type != null) searchParams.append('type', String(params.type))
    if (params?.firearmModelId) searchParams.append('firearmModelId', params.firearmModelId)
    if (params?.partId) searchParams.append('partId', params.partId)
    if (params?.ammoProductId) searchParams.append('ammoProductId', params.ammoProductId)
    if (params?.tag) searchParams.append('tag', params.tag)
    if (params?.tagIds?.length) params.tagIds.forEach((id) => searchParams.append('tagIds', id))
    if (params?.tagMatch) searchParams.append('tagMatch', params.tagMatch)
    if (params?.authorUsername) searchParams.append('authorUsername', params.authorUsername)
    if (params?.pageNumber != null) searchParams.append('pageNumber', String(params.pageNumber))
    if (params?.pageSize != null) searchParams.append('pageSize', String(params.pageSize))
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy)
    const qs = searchParams.toString()
    return requests.get<PagedResult<Post>>(`/posts${qs ? `?${qs}` : ''}`)
  },
  listByFirearmModel: (modelId: string, type?: number) =>
    requests.get<PagedResult<Post>>(`/firearmmodels/${modelId}/posts${type != null ? `?type=${type}` : ''}`).then((r) => r.items),
  listByPart: (partId: string, type?: number) =>
    requests.get<PagedResult<Post>>(`/parts/${partId}/posts${type != null ? `?type=${type}` : ''}`).then((r) => r.items),
  details: (id: string) => requests.get<Post>(`/posts/${id}`),
  listComments: (postId: string) => requests.get<PostCommentDto[]>(`/posts/${postId}/comments`),
  addComment: (postId: string, body: string, parentCommentId?: number | null) =>
    requests.post<PostCommentDto>(`/posts/${postId}/comments`, {
      body,
      parentCommentId: parentCommentId ?? undefined
    }),
  create: (post: PostFormValues) => requests.post<{ id: string }>('/posts', post),
  update: (post: PostFormValues) => requests.put<void>(`/posts/${post.id}`, post),
  delete: (id: string) => requests.del<void>(`/posts/${id}`),
  uploadCover: (file: File) => {
    const formData = new FormData()
    formData.append('File', file)
    // Omit Content-Type so the browser sets multipart boundary (required for ASP.NET IFormFile).
    return axios.post<{ url: string }>('/posts/uploadCover', formData).then(responseBody)
  }
}

const Account = {
  current: () => requests.get<User>('/account'),
  login: (user: UserFormValues) => requests.post<User>('/account/login', user),
  register: (user: UserFormValues) => requests.post<User>('/account/register', user),
  confirmEmail: (userId: string, token: string) =>
    requests.get<{ message: string }>(`/account/confirm-email?userId=${encodeURIComponent(userId)}&token=${encodeURIComponent(token)}`),
  resendConfirmation: () => requests.post<{ message: string }>('/account/resend-confirmation', {}),
  forgotPassword: (email: string) => requests.post<{ message: string }>('/account/forgot-password', { email }),
  resetPassword: (userId: string, token: string, newPassword: string, confirmPassword: string) =>
    requests.post<{ message: string }>('/account/reset-password', { userId, token, newPassword, confirmPassword })
}

const Arduinos = {
  list: () => requests.get<Arduino[]>('/arduinos'),
  details: (id: string) => requests.get<Arduino>(`/arduinos/${id}`),
  create: (arduino: Arduino) => requests.post<void>('/arduinos', arduino),
  update: (arduino: Arduino) => requests.put<void>(`/arduinos/${arduino.id}/${arduino.value}`, arduino),
  delete: (id: string) => axios.delete<void>(`/arduinos/${id}`)
}

const ArduinoHistories = {
  list: () => requests.get<ArduinoHistory[]>('/ArduinoHistory/'),
  details: (id: string) => requests.get<ArduinoHistory>(`/ArduinoHistory/${id}`),
  create: (arduino: ArduinoHistory) => requests.post<void>('/ArduinoHistory', arduino),
  update: (arduino: ArduinoHistory) => requests.put<void>(`/ArduinoHistory/${arduino.id}/${arduino.value}`, arduino),
  delete: (id: string) => axios.delete<void>(`/ArduinoHistory/${id}`)
}

const Motd = {
  get: () => requests.get<MotdDto | null>('/motd'),
  set: (message: string, scheduledExpiresAtUtc?: string | null) =>
    requests.post<MotdDto>('/motd', {
      message,
      ...(scheduledExpiresAtUtc?.trim() ? { scheduledExpiresAtUtc: scheduledExpiresAtUtc.trim() } : {})
    }),
  dismiss: () => requests.post<void>('/motd/dismiss', {}),
  clear: () => requests.del<void>('/motd'),
  history: (pageNumber = 1, pageSize = 25) =>
    requests.get<PagedResult<MotdHistoryItemDto>>('/motd/history', { params: { pageNumber, pageSize } })
}

export interface StatsDto {
  totalFirearmModels: number;
  totalParts: number;
  totalBuilds: number;
}

export interface LeaderboardEntryDto {
  username: string;
  displayName: string;
  mainPhotoUrl: string | null;
  ammoTier: string;
  ammoScore: number;
}

const Profiles = {
  get: (username: string) => requests.get<Profile>(`/profiles/${username}`),
  leaderboard: (limit = 5) =>
    requests.get<LeaderboardEntryDto[]>(`/profiles/leaderboard?limit=${limit}`),
  update: (
    username: string,
    profile: {
      displayName: string;
      bio?: string;
      coverImageUrl?: string | null;
      isVaultPublic?: boolean;
      isWishlistPublic?: boolean;
    }
  ) => requests.put<Profile>(`/profiles/${username}`, profile),
  follow: (username: string) => requests.post(`/profiles/${username}/follow`, {}),
  unfollow: (username: string) => requests.del(`/profiles/${username}/follow`),
  listFollowers: (username: string) => requests.get<Profile[]>(`/profiles/${username}/followers`),
  listFollowing: (username: string) => requests.get<Profile[]>(`/profiles/${username}/following`),
  uploadPhoto: (file: any) => {
    const formData = new FormData()
    formData.append('File', file as any)
    return axios.post<Photo>('photos', formData as any)
  },
  uploadCover: (username: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return axios.post<Profile>(`/profiles/${username}/cover`, formData).then(responseBody)
  },
  setMainPhoto: (id: string) => requests.post(`/photos/${id}/setMain`, {}),
  deletePhoto: (id: string) => requests.del(`/photos/${id}`),
  getActivityFeed: (username: string, pageNumber = 1, pageSize = 20) =>
    requests.get<PagedResult<ProfileActivityItemDto>>(`/profiles/${encodeURIComponent(username)}/activity-feed?pageNumber=${pageNumber}&pageSize=${pageSize}`),
  getPublicVault: (username: string) =>
    requests.get<UserFirearmDto[]>(`/profiles/${encodeURIComponent(username)}/vault`),
  discover: () => requests.get<DiscoverResult>('/profiles/discover'),
  recentUsers: () => requests.get<RecentUserDto[]>('/profiles/recent')
}

const Firearms = {
  list: () => requests.get<Firearm[]>('/firearms'),
  details: (id: string) => requests.get<Firearm>(`/firearms/${id}`),
  create: (firearm: FirearmFormValues) => requests.post<{ id: string }>('/firearms', firearm),
  update: (firearm: FirearmFormValues) => requests.put<void>(`/firearms/${firearm.id}`, firearm),
  delete: (id: string) => requests.del<void>(`/firearms/${id}`),
  addImage: (firearmId: string, file: File) => {
    const formData = new FormData()
    formData.append('File', file)
    return axios.post<FirearmImageDto>(`/firearms/${firearmId}/images`, formData).then(responseBody)
  },
  deleteImage: (firearmId: string, imageId: string) => requests.del<void>(`/firearms/${firearmId}/images/${imageId}`),
  setMainImage: (firearmId: string, imageId: string) => requests.post<void>(`/firearms/${firearmId}/images/${imageId}/setMain`, {})
}

const FirearmModels = {
  list: (params?: { make?: string; platform?: string; caliber?: string; sortBy?: string; tagIds?: string[]; tagMatch?: string; pageNumber?: number; pageSize?: number }) => {
    const searchParams = new URLSearchParams()
    if (params?.make) searchParams.append('make', params.make)
    if (params?.platform) searchParams.append('platform', params.platform)
    if (params?.caliber) searchParams.append('caliber', params.caliber)
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy)
    if (params?.tagIds?.length) params.tagIds.forEach((id) => searchParams.append('tagIds', id))
    if (params?.tagMatch) searchParams.append('tagMatch', params.tagMatch)
    if (params?.pageNumber != null) searchParams.append('pageNumber', String(params.pageNumber))
    if (params?.pageSize != null) searchParams.append('pageSize', String(params.pageSize))
    const qs = searchParams.toString()
    return requests.get<PagedResult<FirearmModel>>(`/firearmmodels${qs ? `?${qs}` : ''}`)
  },
  details: (id: string) => requests.get<FirearmModel>(`/firearmmodels/${id}`),
  create: (model: FirearmModelFormValues) => requests.post<{ id: string }>('/firearmmodels', model),
  update: (id: string, values: Partial<FirearmModelFormValues> & { isVerified?: boolean }) =>
    requests.put<void>(`/firearmmodels/${id}`, values),
  approve: (id: string) => requests.post<void>(`/firearmmodels/${id}/approve`, {}),
  reject: (id: string, reason?: string) => requests.post<void>(`/firearmmodels/${id}/reject`, { reason }),
  uploadImage: (id: string, file: File) => {
    const formData = new FormData()
    formData.append('File', file)
    return axios.post<string>(`/firearmmodels/${id}/images`, formData).then(responseBody)
  },
  hide: (id: string, reason?: string) =>
    requests.post<void>(`/firearmmodels/${id}/hide`, { reason }),
  unhide: (id: string) =>
    requests.post<void>(`/firearmmodels/${id}/unhide`, {}),
  verify: (id: string) =>
    requests.post<void>(`/firearmmodels/${id}/verify`, {}),
  unverify: (id: string) =>
    requests.post<void>(`/firearmmodels/${id}/unverify`, {}),
  getStats: (id: string) =>
    requests.get<{ vaultCount: number; buildCount: number; postCount: number; avgVoteScore: number }>(`/firearmmodels/${id}/stats`),
  getRelated: (id: string, limit = 6) =>
    requests.get<RelatedModelDto[]>(`/firearmmodels/${id}/related?limit=${limit}`),
  delete: (id: string) => requests.del<void>(`/firearmmodels/${id}`),
  listRegistryComments: (id: string) => requests.get<RegistryCommentDto[]>(`/firearmmodels/${id}/registry-comments`),
  addRegistryComment: (id: string, body: string) =>
    requests.post<RegistryCommentDto>(`/firearmmodels/${id}/registry-comments`, { body }),
  addCompatibility: (id: string, cartridgeSpecId: string, isPrimary: boolean) =>
    requests.post<void>(`/firearmmodels/${id}/compatibility`, { cartridgeSpecId, isPrimary }),
  removeCompatibility: (id: string, cartridgeSpecId: string) =>
    requests.del<void>(`/firearmmodels/${id}/compatibility/${cartridgeSpecId}`)
}

const Parts = {
  list: (params?: { authorUsername?: string; tagIds?: string[]; tagMatch?: string; pageNumber?: number; pageSize?: number }) => {
    const searchParams = new URLSearchParams()
    if (params?.authorUsername) searchParams.append('authorUsername', params.authorUsername)
    if (params?.tagIds?.length) params.tagIds.forEach((id) => searchParams.append('tagIds', id))
    if (params?.tagMatch) searchParams.append('tagMatch', params.tagMatch)
    if (params?.pageNumber != null) searchParams.append('pageNumber', String(params.pageNumber))
    if (params?.pageSize != null) searchParams.append('pageSize', String(params.pageSize))
    const qs = searchParams.toString()
    return requests.get<PagedResult<Part>>(`/parts${qs ? `?${qs}` : ''}`)
  },
  listPending: () => requests.get<Part[]>('/parts/pending'),
  create: (part: PartFormValues) => requests.post<{ id: string }>('/parts', part),
  update: (id: string, part: PartFormValues) => requests.put<Part>(`/parts/${id}`, part),
  delete: (id: string) => requests.del<void>(`/parts/${id}`),
  uploadImage: (partId: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return axios.post<Part>(`/parts/${partId}/image`, formData).then(responseBody)
  },
  approve: (id: string) => requests.post<void>(`/parts/${id}/approve`, {}),
  reject: (id: string, reason: string) => requests.post<void>(`/parts/${id}/reject`, { reason }),
  details: (id: string) => requests.get<Part>(`/parts/${id}`)
}

const Builds = {
  list: (params?: { firearmModelId?: string; ownerId?: string; ownerUsername?: string; tagIds?: string[]; tagMatch?: string; pageNumber?: number; pageSize?: number }) => {
    const searchParams = new URLSearchParams()
    if (params?.firearmModelId) searchParams.append('firearmModelId', params.firearmModelId)
    if (params?.ownerId) searchParams.append('ownerId', params.ownerId)
    if (params?.ownerUsername) searchParams.append('ownerUsername', params.ownerUsername)
    if (params?.tagIds?.length) params.tagIds.forEach((id) => searchParams.append('tagIds', id))
    if (params?.tagMatch) searchParams.append('tagMatch', params.tagMatch)
    if (params?.pageNumber != null) searchParams.append('pageNumber', String(params.pageNumber))
    if (params?.pageSize != null) searchParams.append('pageSize', String(params.pageSize))
    const qs = searchParams.toString()
    return requests.get<PagedResult<FirearmBuild>>(`/builds${qs ? `?${qs}` : ''}`)
  },
  details: (id: string) => requests.get<FirearmBuild>(`/builds/${id}`),
  create: (build: FirearmBuildFormValues) => requests.post<{ id: string }>('/builds', build),
  update: (id: string, build: { name?: string; description?: string }) => requests.put<void>(`/builds/${id}`, build),
  delete: (id: string) => requests.del<void>(`/builds/${id}`),
  addPart: (buildId: string, partId: string, notes?: string, quantity?: number, zoneOverride?: string) =>
    requests.post<void>(`/builds/${buildId}/parts`, { partId, notes, quantity: quantity ?? 1, zoneOverride: zoneOverride || null }),
  updatePart: (
    buildId: string,
    buildPartId: string,
    notes?: string,
    quantity?: number,
    zoneOverride?: string,
    positionFields?: {
      xOffsetPercent?: number | null;
      yOffsetPercent?: number | null;
      railPosition?: string | null;
      sortOrder?: number | null;
      clearXOffset?: boolean;
      clearYOffset?: boolean;
      clearRailPosition?: boolean;
      clearSortOrder?: boolean;
    },
    mountType?: string
  ) =>
    requests.put<void>(`/builds/${buildId}/parts/${buildPartId}`, {
      notes,
      quantity: quantity ?? 1,
      zoneOverride: zoneOverride || null,
      mountType: mountType || null,
      ...(positionFields ?? {})
    }),
  removePart: (buildId: string, partId: string) => requests.del<void>(`/builds/${buildId}/parts/${partId}`)
}

/** Vote target types: Post=0, Part=1, FirearmModel=2, Build=3, Comment=4, PostComment=5, Photo=6 */
export const VoteTargetType = {
  Post: 0,
  Part: 1,
  FirearmModel: 2,
  Build: 3,
  Comment: 4,
  PostComment: 5,
  Photo: 6
} as const

const Votes = {
  cast: (targetType: number, targetId: string, value: number) =>
    requests.post<void>('/votes', { targetType, targetId, value }),
  remove: (targetType: number, targetId: string) =>
    requests.delWithBody<void>('/votes', { targetType, targetId })
}

const Flags = {
  list: (params?: FlagListParams) => {
    const searchParams = new URLSearchParams()
    if (params?.status !== undefined && params?.status !== '') { searchParams.append('status', String(params.status)) }
    if (params?.targetType) searchParams.append('targetType', params.targetType)
    if (params?.dateFrom) searchParams.append('dateFrom', params.dateFrom)
    if (params?.dateTo) searchParams.append('dateTo', params.dateTo)
    const qs = searchParams.toString()
    return requests.get<FlagDto[]>(`/flags${qs ? `?${qs}` : ''}`)
  },
  openCount: () => requests.get<number>('/flags/open-count'),
  submit: (body: {
    targetType: number;
    targetId?: string;
    postCommentId?: number;
    reason: string;
  }) => requests.post<{ id: string }>('/flags', body),
  resolve: (id: string, body: { action: string; resolution: string }) =>
    requests.post<void>(`/flags/${id}/resolve`, body)
}

const Moderation = {
  getLog: (params?: ModerationLogParams) =>
    requests.get<PagedResult<ModerationLogDto>>('/moderation/log', { params }),
  exportLog: (params?: ModerationLogParams) => {
    const searchParams = new URLSearchParams()
    if (params?.moderatorUsername != null) searchParams.append('moderatorUsername', params.moderatorUsername)
    if (params?.action != null) searchParams.append('action', params.action)
    if (params?.targetType != null) searchParams.append('targetType', params.targetType)
    if (params?.dateFrom != null) searchParams.append('dateFrom', params.dateFrom)
    if (params?.dateTo != null) searchParams.append('dateTo', params.dateTo)
    const qs = searchParams.toString()
    return axios.get(`${resolveApiBaseUrl()}/moderation/log/export${qs ? `?${qs}` : ''}`, {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${store.commonStore.token}` }
    })
  },
  getModeratorSummary: () =>
    requests.get<ModeratorSummaryDto[]>('/moderation/moderators/summary'),
  getEscalations: () =>
    requests.get<FlagDto[]>('/moderation/escalations'),
  resolveEscalation: (flagId: string, body: { action: string; notes: string }) =>
    requests.post<void>(`/moderation/escalations/${flagId}/resolve`, body),
  grantRole: (userId: string, scope: string) =>
    requests.post<void>('/moderation/roles', { userId, scope }),
  revokeRole: (userId: string) => requests.del<void>(`/moderation/roles/${userId}`)
}

export interface TrendingResult {
  posts: Post[];
  parts: Part[];
  builds: FirearmBuild[];
  firearmModels: FirearmModel[];
}

export interface FeedPageDto {
  items: Post[];
  nextCursor: string | null;
}

const Feed = {
  trending: (limit = 10, days = 7) =>
    requests.get<TrendingResult>(`/feed/trending?limit=${limit}&days=${days}`),
  following: (cursor?: string | null, pageSize = 20, tagIds?: string[]) => {
    const qs = new URLSearchParams({ pageSize: String(pageSize) })
    if (cursor) qs.set('cursor', cursor)
    if (tagIds?.length) tagIds.forEach((id) => qs.append('tagIds', id))
    return requests.get<FeedPageDto>(`/feed/following?${qs.toString()}`)
  }
}

export interface AdminUserDto {
  id: string;
  userName: string;
  email: string;
  displayName: string;
  bio?: string;
  isAdmin: boolean;
  isSuspended: boolean;
  suspendedReason: string | null;
  suspendedUntil: string | null;
  createdOn: string | null;
  ammoScore: number;
  ammoTier: string;
  followersCount: number;
  postCount: number;
  partCount: number;
  buildCount: number;
  flagsUpheldAgainstUser: number;
}

export interface AdminUserDetailDto extends AdminUserDto {
  moderatorRoles: { scope: string; grantedAt: string }[];
  recentFlags: AdminFlagDto[];
  recentFlagsAgainst: AdminFlagDto[];
  recentAdminActions: AdminActionLogEntryDto[];
  signalCounters: SignalCountersDto;
  spamCandidateCount: number;
}

export interface AdminFlagDto {
  id: string;
  targetType: number;
  targetId: string;
  reason: string;
  status: number;
  resolution: string | null;
  createdAt: string;
  reporterUsername: string;
}

export interface AdminActionLogEntryDto {
  id: string;
  action: string;
  detail: string;
  createdAt: string;
  adminUsername: string;
}

export interface SignalCountersDto {
  postVotesReceived: number;
  postVotesLost: number;
  partVotesReceived: number;
  partVotesLost: number;
  buildVotesReceived: number;
  buildVotesLost: number;
  modelVotesReceived: number;
  modelVotesLost: number;
  approvedPartsCount: number;
  approvedModelsCount: number;
}

export interface AdminUserParams {
  q?: string;
  tier?: string;
  isSuspended?: boolean;
  isAdmin?: boolean;
  pageNumber?: number;
  pageSize?: number;
}

export interface EditUserValues {
  username?: string;
  displayName?: string;
  email?: string;
  bio?: string;
  isAdmin?: boolean;
  isSuspended?: boolean;
  suspendedReason?: string;
  suspendedUntil?: string | null;
}

const Tags = {
  list: (params?: { category?: number; q?: string }) =>
    requests.get<TagDto[]>('/tags', { params }),
  listPending: () =>
    requests.get<TagDto[]>('/tags/pending'),
  create: (values: { name: string; displayName: string; category: number }) =>
    requests.post<TagDto>('/tags', values),
  approve: (id: string) =>
    requests.post<void>(`/tags/${id}/approve`, {}),
  reject: (id: string) =>
    requests.post<void>(`/tags/${id}/reject`, {}),
  applyTag: (body: { tagId: string; targetType: number; targetId: string }) =>
    requests.post<TagDto>('/entitytags', body),
  removeTag: (body: { tagId: string; targetType: number; targetId: string }) =>
    requests.delWithBody<void>('/entitytags', body)
}

const Admin = {
  listUsers: (params: AdminUserParams) =>
    requests.get<PagedResult<AdminUserDto>>('/admin/users', { params }),
  getUser: (username: string) =>
    requests.get<AdminUserDetailDto>(`/admin/users/${encodeURIComponent(username)}`),
  editUser: (username: string, values: EditUserValues) =>
    requests.patch<AdminUserDto>(`/admin/users/${encodeURIComponent(username)}`, { ...values, username }),
  adjustReputation: (username: string, body: { username: string; delta: number; detail: string; isVisibleToUser?: boolean }) =>
    requests.post<AdminUserDto>(`/admin/users/${encodeURIComponent(username)}/reputation/adjust`, { ...body, username }),
  deleteUser: (username: string) =>
    requests.del<void>(`/admin/users/${encodeURIComponent(username)}`),
  resetPassword: (username: string, dto: { newPassword: string; confirmPassword: string }) =>
    requests.post<void>(`/admin/users/${encodeURIComponent(username)}/reset-password`, dto),
  getOrphanedContent: (params: OrphanedContentParams) =>
    requests.get<PagedResult<OrphanedContentDto>>('/admin/content/orphaned', { params }),
  reassignContent: (body: ReassignContentBody) =>
    requests.post<void>('/admin/content/reassign', body),
  bulkReassign: (body: BulkReassignBody) =>
    requests.post<BulkReassignResult>('/admin/content/bulk-reassign', body),
  getReassignmentLog: (params: PaginationParams) =>
    requests.get<PagedResult<AdminActionLogDto>>('/admin/content/reassignment-log', { params }),
  listInternalTags: () =>
    requests.get<InternalTagSummaryDto[]>('/admin/tags/internal'),
  getInternalTagContent: (tagName: string, params: PaginationParams) =>
    requests.get<PagedResult<InternalTagContentDto>>(
      `/admin/tags/internal/${encodeURIComponent(tagName)}/content`,
      { params }
    ),
  applyInternalTag: (body: { tagName: string; targetType: number; targetId: string }) =>
    requests.post<InternalTagContentDto>('/admin/tags/internal/apply', body),
  removeInternalTag: (entityTagId: string) =>
    requests.del<void>(`/admin/tags/internal/${entityTagId}`)
}

const Stats = {
  get: () => requests.get<StatsDto>('/stats')
}

export interface RelatedModelDto {
  id: string;
  make: string;
  model: string;
  platform: string;
  caliber: string;
  coverImageUrl: string | null;
  isVerified: boolean;
  buildCount: number;
}

export interface RegistryFillResult {
  make: string | null;
  model: string | null;
  platform: string | null;
  caliber: string | null;
  actionType: string | null;
  yearIntroduced: number | null;
  description: string | null;
}

export interface BuildFillResult {
  description: string | null;
}

export interface PartFillResult {
  manufacturer: string | null;
  description: string | null;
  category: string | null;
  msrpUsd: number | null;
}

export interface TagFillResult {
  tags: TagDto[];
}

const AiAssist = {
  registryFill: (query: string) =>
    requests.post<RegistryFillResult>('/ai/registry-fill', { query }),
  buildFill: (name: string, firearmModel?: string) =>
    requests.post<BuildFillResult>('/ai/build-fill', { name, firearmModel: firearmModel ?? '' }),
  partFill: (name: string, partNumber?: string) =>
    requests.post<PartFillResult>('/ai/part-fill', { name, partNumber: partNumber ?? '' }),
  tagFill: (targetType: number, name: string, description?: string) =>
    requests.post<TagFillResult>('/ai/tag-fill', { targetType, name, description: description ?? '' }),
  ollamaChat: (
    userMessages: string[],
    assistantMessages: string[],
    opts?: { model?: string; system?: string; temperature?: number; topP?: number; maxTokens?: number }
  ) =>
    requests.post<{ response: string }>('/ai/ollama-chat', {
      userMessages,
      assistantMessages,
      model: opts?.model,
      system: opts?.system,
      temperature: opts?.temperature,
      topP: opts?.topP,
      maxTokens: opts?.maxTokens
    }),
  ollamaModels: () =>
    requests.get<{ models: string[] }>('/ai/ollama-models')
}

const Search = {
  search: (q: string, type?: string | null) =>
    requests.get<SearchResultDto>('/search', { params: type ? { q, type } : { q } })
}

const Notifications = {
  list: (pageNumber = 1, pageSize = 20) =>
    requests.get<PagedResult<NotificationItemDto>>(`/notifications?pageNumber=${pageNumber}&pageSize=${pageSize}`),
  unreadCount: () => requests.get<number>('/notifications/unread-count'),
  markRead: (ids: string[]) => requests.post<void>('/notifications/mark-read', { ids }),
  markAllRead: () => requests.post<void>('/notifications/mark-all-read', {})
}

export interface WishlistItemDto {
  id: string;
  firearmModelId: string;
  firearmModelMake: string;
  firearmModelModel: string;
  firearmModelPlatform?: string | null;
  firearmModelCoverImageUrl?: string | null;
  addedAt: string;
  notes?: string | null;
  isPublic: boolean;
}

const Wishlist = {
  list: () => requests.get<WishlistItemDto[]>('/wishlist'),
  add: (firearmModelId: string, notes?: string, isPublic = true) =>
    requests.post<WishlistItemDto>('/wishlist', { firearmModelId, notes, isPublic }),
  patch: (id: string, body: { isPublic?: boolean; notes?: string | null }) =>
    requests.patch<WishlistItemDto>(`/wishlist/${id}`, body),
  remove: (id: string) => requests.del<void>(`/wishlist/${id}`),
  getPublic: (username: string) => requests.get<WishlistItemDto[]>(`/wishlist/${username}`)
}

const GunVault = {
  list: () => requests.get<UserFirearmDto[]>('/gunvault'),
  add: (firearmModelId: string, notes?: string) =>
    requests.post<UserFirearmDto>('/gunvault', { firearmModelId, notes }),
  remove: (id: string) => requests.del<void>(`/gunvault/${id}`),
  pin: (id: string, pinned: boolean) =>
    requests.patch<void>(`/gunvault/${id}/pin`, { pinned })
}

const ContentPhotos = {
  list: (ownerType: number, ownerId: string) =>
    requests.get<ContentPhotoDto[]>(`/ContentPhotos/${ownerType}/${ownerId}`),
  upload: (ownerType: number, ownerId: string, file: File) => {
    const fd = new FormData()
    fd.append('File', file)
    return axios.post(`/ContentPhotos/${ownerType}/${ownerId}`, fd).then(responseBody) as Promise<ContentPhotoDto>
  },
  delete: (id: string) => requests.del<void>(`/ContentPhotos/${id}`),
  setMain: (id: string) => requests.post<void>(`/ContentPhotos/${id}/set-main`, {}),
  updateCaption: (id: string, caption: string) =>
    requests.patch<ContentPhotoDto>(`/ContentPhotos/${id}/caption`, { caption }),
  vote: (id: string) =>
    requests.post<{ voted: boolean; voteCount: number }>(`/ContentPhotos/${id}/vote`, {}),
  getComments: (id: string) => requests.get<PhotoCommentDto[]>(`/ContentPhotos/${id}/comments`),
  addComment: (id: string, body: string, parentCommentId?: string) =>
    requests.post<PhotoCommentDto>(`/ContentPhotos/${id}/comments`, { body, parentCommentId }),
  deleteComment: (commentId: string) => requests.del<void>(`/ContentPhotos/comments/${commentId}`)
}

const FeatureFlags = {
  list: () => requests.get<FeatureFlagDto[]>('/featureflags'),
  set: (key: string, isEnabled: boolean) =>
    requests.patch<FeatureFlagDto>(`/featureflags/${key}`, { isEnabled })
}

const CartridgeSpecs = {
  list: (params?: { searchText?: string; type?: string; pageNumber?: number; pageSize?: number }) => {
    const searchParams = new URLSearchParams()
    if (params?.searchText) searchParams.append('searchText', params.searchText)
    if (params?.type) searchParams.append('type', params.type)
    if (params?.pageNumber != null) searchParams.append('pageNumber', String(params.pageNumber))
    if (params?.pageSize != null) searchParams.append('pageSize', String(params.pageSize))
    const qs = searchParams.toString()
    return requests.get<PagedResult<CartridgeSpecDto>>(`/cartridgespecs${qs ? `?${qs}` : ''}`)
  },
  details: (id: string) => requests.get<CartridgeSpecDto>(`/cartridgespecs/${id}`),
  create: (values: CartridgeSpecFormValues) => requests.post<CartridgeSpecDto>('/cartridgespecs', values),
  approve: (id: string) => requests.post<void>(`/cartridgespecs/${id}/approve`, {}),
  reject: (id: string, reason?: string) => requests.post<void>(`/cartridgespecs/${id}/reject`, { reason })
}

const AmmoProducts = {
  list: (params?: { cartridgeSpecId?: string; searchText?: string; bulletType?: string; sortBy?: string; pageNumber?: number; pageSize?: number }) => {
    const searchParams = new URLSearchParams()
    if (params?.cartridgeSpecId) searchParams.append('cartridgeSpecId', params.cartridgeSpecId)
    if (params?.searchText) searchParams.append('searchText', params.searchText)
    if (params?.bulletType) searchParams.append('bulletType', params.bulletType)
    if (params?.sortBy) searchParams.append('sortBy', params.sortBy)
    if (params?.pageNumber != null) searchParams.append('pageNumber', String(params.pageNumber))
    if (params?.pageSize != null) searchParams.append('pageSize', String(params.pageSize))
    const qs = searchParams.toString()
    return requests.get<PagedResult<AmmoProductDto>>(`/ammoproducts${qs ? `?${qs}` : ''}`)
  },
  details: (id: string) => requests.get<AmmoProductDto>(`/ammoproducts/${id}`),
  create: (values: AmmoProductFormValues) => requests.post<AmmoProductDto>('/ammoproducts', values),
  approve: (id: string) => requests.post<void>(`/ammoproducts/${id}/approve`, {}),
  reject: (id: string, reason?: string) => requests.post<void>(`/ammoproducts/${id}/reject`, { reason })
}

const ReloaderRecipes = {
  list: (params?: { cartridgeSpecId?: string; powderType?: string; bulletWeightGr?: number; bulletWeightGrMin?: number; bulletWeightGrMax?: number; intendedUse?: string; isFlagged?: boolean; pageNumber?: number; pageSize?: number }) => {
    const searchParams = new URLSearchParams()
    if (params?.cartridgeSpecId) searchParams.append('cartridgeSpecId', params.cartridgeSpecId)
    if (params?.powderType) searchParams.append('powderType', params.powderType)
    if (params?.bulletWeightGr != null) searchParams.append('bulletWeightGr', String(params.bulletWeightGr))
    if (params?.bulletWeightGrMin != null) searchParams.append('bulletWeightGrMin', String(params.bulletWeightGrMin))
    if (params?.bulletWeightGrMax != null) searchParams.append('bulletWeightGrMax', String(params.bulletWeightGrMax))
    if (params?.intendedUse) searchParams.append('intendedUse', params.intendedUse)
    if (params?.isFlagged != null) searchParams.append('isFlagged', String(params.isFlagged))
    if (params?.pageNumber != null) searchParams.append('pageNumber', String(params.pageNumber))
    if (params?.pageSize != null) searchParams.append('pageSize', String(params.pageSize))
    const qs = searchParams.toString()
    return requests.get<PagedResult<ReloaderRecipeDto>>(`/reloaderrecipes${qs ? `?${qs}` : ''}`)
  },
  details: (id: string) => requests.get<ReloaderRecipeDto>(`/reloaderrecipes/${id}`),
  create: (values: ReloaderRecipeFormValues) => requests.post<ReloaderRecipeDto>('/reloaderrecipes', values),
  validate: (id: string, data: RecipeValidationData) => requests.post(`/reloaderrecipes/${id}/validate`, data),
  unvalidate: (id: string) => requests.del(`/reloaderrecipes/${id}/validate`),
  addComment: (id: string, body: string) => requests.post(`/reloaderrecipes/${id}/comments`, { body }),
  flag: (id: string, reason: string) => requests.post(`/reloaderrecipes/${id}/flag`, { reason })
}

const SlopFeed = {
  /** Mixed Shoot / Post / Blog discover feed (cursor pagination). */
  getMixedFeed: (limit = 10, cursor?: string | null) => {
    const params: Record<string, string | number> = { limit }
    if (cursor) params.cursor = cursor
    return requests.get<MixedFeedResultDto>('/slop-feed', { params })
  },
  getFeed: (page = 1, pageSize = 20) => {
    const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
    return requests.get<SlopPostDto[]>(`/slopfeed?${qs.toString()}`)
  },
  getDetails: (id: string) => requests.get<SlopPostDto>(`/slopfeed/${id}`),
  upload: (formData: FormData) =>
    axios.post<SlopPostDto>(`${resolveApiBaseUrl()}/slopfeed`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(responseBody),
  addEmbed: (data: { embedSourceUrl: string; title: string; description: string }) =>
    requests.post<SlopPostDto>('/slopfeed/embed', data),
  deletePost: (id: string) => requests.del<void>(`/slopfeed/${id}`),
  toggleLike: (id: string) =>
    requests.post<{ newLikeCount: number; isLiked: boolean }>(`/slopfeed/${id}/like`, {}),
  getComments: (id: string) =>
    requests.get<SlopPostCommentDto[]>(`/slopfeed/${id}/comments`),
  addComment: (id: string, body: string, parentCommentId?: number) =>
    requests.post<SlopPostCommentDto>(`/slopfeed/${id}/comments`, { body, parentCommentId: parentCommentId ?? null }),
  deleteComment: (postId: string, commentId: number) =>
    requests.del<void>(`/slopfeed/${postId}/comments/${commentId}`)
}

const Shoots = {
  create: (formData: FormData) =>
    axios.post<ShootDto>(`${resolveApiBaseUrl()}/shoots`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(responseBody),
  details: (id: string) => requests.get<ShootDto>(`/shoots/${id}`),
  deleteShoot: (id: string) => requests.del<void>(`/shoots/${id}`),
  toggleLike: (id: string) =>
    requests.post<ShootLikeResultDto>(`/shoots/${id}/like`, {})
}

const ConsentText = {
  get: (key: string) => requests.get<ConsentTextConfigDto>(`/consenttext/${key}`),
  set: (values: ConsentTextFormValues) => requests.post<ConsentTextConfigDto>('/consenttext', values)
}

const LegalText = {
  getByKey: (key: string) => requests.get<LegalTextDto>(`/legaltexts/${key}`),
  getAll: () => requests.get<LegalTextDto[]>('/legaltexts'),
  upsert: (key: string, body: string) => requests.put<LegalTextDto>(`/legaltexts/${key}`, { body })
}

const agent = {
  Activities,
  Account,
  Profiles,
  Posts,
  Firearms,
  FirearmModels,
  Parts,
  ContentPhotos,
  Builds,
  Votes,
  VoteTargetType,
  Flags,
  Moderation,
  Feed,
  Admin,
  Tags,
  Stats,
  AiAssist,
  Search,
  GunVault,
  Wishlist,
  Notifications,
  Arduinos,
  ArduinoHistories,
  Motd,
  FeatureFlags,
  CartridgeSpecs,
  AmmoProducts,
  ReloaderRecipes,
  SlopFeed,
  Shoots,
  ConsentText,
  LegalText
}

export default agent
